import './styles.scss';

import { Chip } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Switch from '@mui/material/Switch';
import Hashids from 'hashids';
import React, { useEffect, useState } from 'react';
import SweetAlert from 'react-bootstrap-sweetalert';
import BootstrapTable from 'react-bootstrap-table-next';
import cellEditFactory from 'react-bootstrap-table2-editor';
import paginationFactory, {
  PaginationProvider,
  PaginationTotalStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import { isMobile } from 'react-device-detect';
import Lightbox from 'react-image-lightbox';
import { connect } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';

import AlphabetPicker from '@/components/alphabet-picker';
import Breadcrumb from '@/components/common/breadcrumb.component';
import DebounceSearchBar from '@/components/debounce-search-bar';
import Icon from '@/components/icon';
import NoDataIndicator from '@/components/no-data-indicator';
import Button from '@/components/overrides/button';
import StandalonePagination from '@/components/pagination/standalone';
import RowMoreOptions from '@/components/table/row-more-options';
import TableLoader from '@/components/table-loader';
import VitolaEditor from '@/components/vitolas/editor';
import VitolaMerger from '@/components/vitolas/merger';
import { getAllVitolas } from '@/redux/actions/cigar.actions';
import { updateVitola } from '@/utils/actions/cigar';

const hashids = new Hashids('', 12);

const defaultSorted = [
  {
    dataField: 'brand',
    order: 'asc',
  },
];

const cellEditProps = {
  mode: 'dbclick',
  beforeSaveCell(oldValue, newValue, row, column, done) {
    console.log(`Before save for ${column.dataField}`);
    // TODO: call done() on success or done(false) on failure
  },
  afterSaveCell: (oldValue, newValue, row, column) => {
    console.log(`After save for ${column.dataField}`);
  },
};

const VitolaTable = (props) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [vitolas, setVitolas] = useState([]);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(50);
  const [searchText, setSearchText] = useState('');
  const [pendingStatus, setPendingStatus] = useState('all');
  const [tableChangeType, setTableChangeType] = useState(null);
  const [message, setMessage] = useState({
    show: false,
    title: '',
    text: '',
    type: '',
  });
  const [dialogModal, setDialogModal] = useState({
    show: false,
    title: '',
    text: '',
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedLightboxImageUrl, setSelectedLightboxImageUrl] = useState('');
  const [selectedVitola, setSelectedVitola] = useState(null);
  const [selectedVitolas, setSelectedVitolas] = useState([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showAddVitolaModal, setShowAddVitolaModal] = React.useState(false);
  const [toggler, setToggler] = useState(false);

  // useEffect(() => {
  //   // Only run once when the route loads to re-populate the table if necessary
  //   if (searchParams?.has('q')) {
  //     setTimeout(() => {
  //       const query = searchParams.get('q');
  //       if (query !== searchText) {
  //         // FIXME Any way to prevent default fetching of handleTableChange on desktop?
  //         setSearchText(query);
  //         handleTableChange(tableChangeType, {
  //           page,
  //           sizePerPage,
  //           searchText: query,
  //         });
  //       }
  //     }, 250);
  //   }
  // }, []);

  useEffect(() => {
    if (searchParams?.has('id')) {
      props.getAllVitolas({
        page: 1,
        limit: 10,
        id: hashids.decode(searchParams.get('id'))[0],
      });
    } else if (searchParams?.has('cigar_id')) {
      // console.log('Getting vitolas for cigar:', hashids.decode(searchParams.get('cigar_id'))[0]);
      props.getAllVitolas({
        page: 1,
        limit: 10,
        cigarId: hashids.decode(searchParams.get('cigar_id'))[0],
      });
    } else {
      props.getAllVitolas({ page: 1, limit: sizePerPage });
    }
  }, [searchParams]);

  // useEffect(() => {
  //   const params = new URLSearchParams(location.search);
  //   if (searchText && searchText.length > 0) {
  //     params.set('q', searchText);
  //     navigate(`${location.pathname}?${params.toString()}`);
  //   } else {
  //     params.delete('q');
  //     navigate(`${location.pathname}`);
  //   }
  // }, [searchText]);

  useEffect(() => {
    let { vitolas: vitolasFromManager } = props.CigarManager;
    if (vitolasFromManager) {
      if (!Array.isArray(vitolasFromManager)) {
        console.debug('Got a single vitola');
        vitolasFromManager = [vitolasFromManager];
      }
      setVitolas(vitolasFromManager);
    }
  }, [props.CigarManager]);

  const activeFormatter = (pending, vitola) => (
    <span>
      <Switch
        checked={!pending}
        onChange={(e) => {
          vitola.pending = !e.target.checked;
          updateVitola(vitola).then((updatedVitola) => {
            handleRowChange(updatedVitola);
          });
        }}
        color="secondary"
      />
    </span>
  );

  const imageFormatter = (cell, row) => {
    const vitola = row;
    return vitola.image_url ? (
      <img
        alt={vitola.formatted_name}
        src={vitola.image_url}
        style={{ width: 240, cursor: 'pointer' }}
        onClick={() => {
          console.log('Viewing vitola image:', vitola.image_url);
          setSelectedLightboxImageUrl(vitola.image_url);
          setToggler(true);
        }}
      />
    ) : null;
  };

  const nameFormatter = (formattedName, vitola) => {
    return `${vitola.cigar.full_name} ${formattedName}`;
  };

  const handlePageChange = (e, index) => {
    setPage(index);
    const pendingStatusLocal =
      pendingStatus === 'pending' || pendingStatus === 'approved'
        ? pendingStatus
        : null;
    props.getAllVitolas({
      page: index + 1,
      limit: sizePerPage,
      q: searchText?.length > 0 ? searchText : null,
      pending: pendingStatusLocal,
    });
  };

  const editBtnFormatter = (cell, row, index) => (
    <div style={{ minWidth: 68 }}>
      <div id={`dropdown-actions-${index}`} className="dropdown-actions d-none">
        <Icon
          name="edit2"
          className="ml-1 mr-3"
          style={{
            cursor: 'pointer',
            display: 'inline',
            height: 18,
            width: 18,
          }}
          onClick={() => {
            setSelectedVitola(row);
            setShowAddVitolaModal(true);
          }}
        />
        {actionDropdown(index, row)}
      </div>
    </div>
  );

  const handleRowChange = (updatedVitola) => {
    setVitolas((prev) =>
      prev.map((vitola) =>
        vitola.id === updatedVitola.id
          ? { ...vitola, ...updatedVitola }
          : vitola
      )
    );
    updateRecords();
  };

  const handleRowDelete = (deletedVitola: any) => {
    const updatedVitolas = vitolas.filter(
      (vitola: any) => vitola.id !== deletedVitola.id
    );
    setVitolas([...updatedVitolas]);
  };

  const updateRecords = () => {
    if (tableChangeType) {
      handleTableChange(tableChangeType, { page, sizePerPage, searchText });
      setTimeout(() => {
        handleTableChange(tableChangeType, { page, sizePerPage, searchText });
      }, 50);
    }
  };

  const handleTableChange = (
    type,
    { page: newPage, sizePerPage: newSize, searchText: newSearchText }
  ) => {
    console.log('Size per page:', sizePerPage, newSize);
    const pendingStatusLocal =
      pendingStatus === 'pending' || pendingStatus === 'approved'
        ? pendingStatus
        : null;
    props.getAllVitolas({
      page: newPage,
      limit: newSize,
      query: newSearchText?.length > 0 ? newSearchText : null,
      pending: pendingStatusLocal,
    });
    setTableChangeType(type);
    setPage(newPage);
    setSizePerPage(newSize);
    setSearchText(newSearchText);
  };

  const closeAlert = () => {
    setMessage({ show: false, title: '', text: '', type: '' });
  };

  const deleteVitola = () => {
    setDialogModal({
      show: true,
      title: 'Are you sure you want to delete?',
      text: 'This will delete the vitola and cannot be undone. (If associated with sessions or reviews, you may need to merge instead.)',
    });
  };

  const deleteVitolaConfirmed = () => {
    setMessage({
      type: 'info',
      show: true,
      title: 'Under Development',
      text: "This feature isn't available yet. Consider merging the vitola instead.",
    });
  };

  const actionDropdown = (index, vitola) => (
    <RowMoreOptions
      row={index}
      items={[
        {
          type: 'MenuItem',
          onClick: () => {
            // Duplicate action logic
            alert('Coming soon!');
          },
          title: 'Duplicate',
        },
        /* Legacy Algolia action; retained for rollback.
        {
          type: 'MenuItem',
          onClick: () => {
            axios
              .post(
                `${Constants.apiPath}/actions/cigars/${vitola.cigar_id}/search/index`
              )
              .then(() => {
                Toast.success(
                  <ToastContent
                    message="Successfully re-indexed"
                    title={vitola.formatted_name}
                  />,
                  {
                    position: 'top-right',
                  }
                );
              });
          },
          title: 'Re-index in Search',
        },
        */
        {
          type: 'Divider',
        },
        {
          type: 'MenuItem',
          onClick: () => {
            setSelectedVitola(vitola);
            setShowMergeModal(true);
          },
          title: 'Merge',
        },
        {
          type: 'MenuItem',
          onClick: () => {
            // Delete action
            alert(
              'Coming soon! For now, toggle off the "Active" status of the vitola.'
            );
          },
          style: { color: '#ff4700' },
          title: 'Delete',
        },
      ]}
      onShow={() => {
        const dropdownActions = document.querySelector(
          `#dropdown-actions-${index}`
        );
        if (dropdownActions) {
          dropdownActions.classList.add('d-none');
        }
      }}
    />
  );

  const renderFilterOptions = () => (
    <SwipeableDrawer
      anchor="right"
      open={showFilterDrawer}
      onClose={() => setShowFilterDrawer(false)}
    >
      <div
        style={{
          width: isMobile ? 'calc(100vw - 40px)' : 300,
          margin: '0 20px 30px',
        }}
      >
        <div style={{ height: 20 }} />
        <FormLabel component="legend">By Brand</FormLabel>
        <div
          className="select-wrapper"
          style={{ maxWidth: 'calc(100% - 40px)' }}
        >
          <AsyncSelect
            value={selectedBrand || { label: 'All Brands', value: -1 }}
            onChange={(value) => setSelectedBrand(value)}
            loadOptions={undefined /* Provide your loadShops function */}
            defaultOptions={[{ label: 'All Brands', value: -1 }]}
            isOptionSelected={(option) => selectedBrand === option}
          />
        </div>
        <FormLabel component="legend" style={{ marginTop: 12 }}>
          By Status
        </FormLabel>
        <RadioGroup
          row
          aria-label="approval status"
          name="approval-status"
          value={pendingStatus || 'all'}
          onChange={(e) => setPendingStatus(e.target.value)}
        >
          <FormControlLabel
            value="all"
            control={<Radio color="primary" />}
            label="All"
            labelPlacement="end"
          />
          <FormControlLabel
            value="approved"
            control={<Radio color="primary" />}
            label="Approved"
            labelPlacement="end"
          />
          <FormControlLabel
            value="pending"
            control={<Radio color="primary" />}
            label="Pending"
            labelPlacement="end"
          />
        </RadioGroup>
        <div
          style={{
            display: 'flex',
            position: 'fixed',
            bottom: 0,
            backgroundColor: 'white',
            width: 300,
            padding: '10px 0',
            margin: 'auto',
          }}
        >
          <Button
            onClick={() => {
              setShowFilterDrawer(false);
              // resetFilters();
            }}
            style={{
              display: 'block',
              flex: 1,
              marginRight: 10,
              textAlign: 'center',
            }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            color="secondary"
            style={{ display: 'block', flex: 1, textAlign: 'center' }}
            onClick={() => onSaveModal('filter')}
          >
            Apply
          </Button>
        </div>
      </div>
    </SwipeableDrawer>
  );

  const dialogModalComponent = () => (
    <Dialog
      open={dialogModal.show}
      onClose={closeDialogModal}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{dialogModal.title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {dialogModal.text}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialogModal}>Cancel</Button>
        <Button
          onClick={() => {
            closeDialogModal();
            deleteVitolaConfirmed();
          }}
          variant="outlined"
          color="danger"
          autoFocus
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  const closeDialogModal = () => {
    setDialogModal({ show: false, title: '', text: '' });
  };

  const onOpenModal = (type, data) => {
    // Implement modal opening logic here (e.g. set state to show a modal and store selected data)
  };

  const onCloseModal = (type) => {
    // Implement modal closing logic here
  };

  const onSaveModal = (type) => {
    // Implement modal saving logic here
  };

  const pageButtonRenderer = ({ page: currentPage, active, onPageChange }) => {
    const handleClick = (e) => {
      e.preventDefault();
      onPageChange(currentPage);
    };
    const classname = active
      ? 'btn btn-secondary'
      : 'btn btn-outline-secondary';
    return (
      <li className="page-item pl-1" key={currentPage}>
        <a href="#" onClick={handleClick} className={classname}>
          {currentPage}
        </a>
      </li>
    );
  };

  const renderAddVitolaModal = () => (
    <VitolaEditor
      open={showAddVitolaModal}
      vitola={selectedVitola}
      onClose={() => setShowAddVitolaModal(false)}
      onSave={(vitola) => {
        updateVitola(vitola).then((v) => {
          handleRowChange(v);
        });
      }}
    />
  );

  const clearQueryParams = () => {
    navigate(location.pathname, { replace: true });
  };

  return (
    <div>
      <SweetAlert
        show={message.show}
        type={message.type}
        title={message.title}
        onConfirm={closeAlert}
      >
        {message.text}
      </SweetAlert>

      <Breadcrumb title="Vitola Manager" label="Manager" parent="Cigars" />

      <div className="container-fluid">
        {searchParams?.get('filter') && (
          <Chip
            size="small"
            label={searchParams?.get('filter')}
            onClick={clearQueryParams}
            onDelete={clearQueryParams}
          />
        )}
        <div className="row">
          <div className="col-sm-12">
            <div
              className="mb-0"
              style={
                isMobile ? { background: 'transparent', boxShadow: 'none' } : {}
              }
            >
              <div className="datatable-react">
                <ToolkitProvider
                  search
                  keyField="id"
                  data={vitolas}
                  columns={[
                    {
                      dataField: 'pending',
                      text: 'Active',
                      formatter: activeFormatter,
                      sort: false,
                    },
                    {
                      dataField: 'images',
                      text: '',
                      formatter: imageFormatter,
                      sort: false,
                      editable: false,
                    },
                    {
                      dataField: 'formatted_name',
                      text: 'Name',
                      formatter: nameFormatter,
                      sort: true,
                    },
                    { dataField: 'alias', text: 'Alias', sort: true },
                    { dataField: 'shape', text: 'Shape', sort: true },
                    { dataField: 'size', text: 'Size', sort: true },
                    { dataField: 'awards', text: 'Recognition', sort: true },
                    {
                      text: '',
                      formatter: editBtnFormatter,
                      sort: false,
                      editable: false,
                    },
                  ]}
                >
                  {(toolkitprops) => (
                    <div>
                      {!isMobile && (
                        <div style={{ flex: 1, marginTop: 10 }}>
                          <DebounceSearchBar
                            {...toolkitprops.searchProps}
                            value={searchText}
                            placeholder="Search vitolas..."
                            // onChange={() => updateState({ page: 1 })}
                          />
                        </div>
                      )}
                      {isMobile ? (
                        <div>
                          {vitolas.map((cigar, i) => {
                            let cigarImageFull = '';
                            if (cigar.images) {
                              for (let j = 0; j < cigar.images.length; j++) {
                                const image = cigar.images[j];
                                if (image.image_type === 0) {
                                  cigarImageFull = image.image_url;
                                }
                              }
                            }
                            return (
                              <div
                                key={i}
                                className="col-sm-12 col-md-6 col-lg-4 col-xl-3"
                                style={{ padding: 0, display: 'inline-block' }}
                              >
                                <div className="card height-equal">
                                  <div className="card-body native-image-bg">
                                    <span style={{ float: 'right' }}>
                                      {actionDropdown(i, cigar)}
                                    </span>
                                    <div className="row">
                                      <div className="col-4">
                                        <img
                                          src={cigarImageFull}
                                          style={{ width: '100%' }}
                                          onClick={() => {
                                            setSelectedImageUrl(cigarImageFull);
                                          }}
                                        />
                                      </div>
                                      <div className="col-8">
                                        <h4>
                                          {cigar.brand} {cigar.name}
                                        </h4>
                                        <p>
                                          <strong>Origin:</strong>{' '}
                                          {cigar.origin}
                                        </p>
                                        <p>
                                          <strong>Wrapper:</strong>{' '}
                                          {cigar.wrapper}
                                        </p>
                                        <p>
                                          <strong>Binder:</strong>{' '}
                                          {cigar.binder}
                                        </p>
                                        <p>
                                          <strong>Filler:</strong>{' '}
                                          {cigar.filler}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    className="card-footer"
                                    style={{
                                      flexDirection: 'row',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <button
                                      type="button"
                                      className="btn btn-primary ml-2"
                                      style={{ float: 'right' }}
                                      onClick={() =>
                                        onOpenModal('cigar', cigar)
                                      }
                                    >
                                      {'Edit'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <PaginationProvider
                          pagination={paginationFactory({
                            pageButtonRenderer,
                            page,
                            sizePerPage,
                            custom: true,
                            totalSize: props.CigarManager.totalSize,
                            showTotal: true,
                          })}
                        >
                          {({ paginationProps, paginationTableProps }) => (
                            <div>
                              <div>
                                <AlphabetPicker
                                  style={{ margin: '12px 0 auto' }}
                                  onClick={(selection) => {
                                    // TODO Filter the results on the server by value
                                    alert('Coming soon!');
                                  }}
                                />
                              </div>
                              <BootstrapTable
                                remote
                                keyField="id"
                                data={vitolas}
                                columns={[
                                  {
                                    dataField: 'pending',
                                    text: 'Active',
                                    formatter: activeFormatter,
                                    sort: false,
                                  },
                                  {
                                    dataField: 'images',
                                    text: '',
                                    formatter: imageFormatter,
                                    sort: false,
                                    editable: false,
                                  },
                                  {
                                    dataField: 'formatted_name',
                                    text: 'Name',
                                    sort: true,
                                  },
                                  {
                                    dataField: 'alias',
                                    text: 'Alias',
                                    sort: true,
                                  },
                                  {
                                    dataField: 'shape',
                                    text: 'Shape',
                                    sort: true,
                                  },
                                  {
                                    dataField: 'dimen',
                                    text: 'Gauge',
                                    sort: true,
                                  },
                                  {
                                    dataField: 'awards',
                                    text: 'Awards',
                                    sort: true,
                                  },
                                  {
                                    text: '',
                                    formatter: editBtnFormatter,
                                    sort: false,
                                    editable: false,
                                  },
                                ]}
                                selectRow={{ mode: 'checkbox' }}
                                rowEvents={{
                                  onMouseEnter: (e, row, index) => {
                                    document
                                      .querySelector(
                                        `#dropdown-actions-${index}`
                                      )
                                      ?.classList.remove('d-none');
                                  },
                                  onMouseLeave: (e, row, index) => {
                                    document
                                      .querySelector(
                                        `#dropdown-actions-${index}`
                                      )
                                      ?.classList.add('d-none');
                                  },
                                  // onDoubleClick: (e, row, index) => {
                                  //   // Double click event if needed
                                  // },
                                }}
                                defaultSorted={defaultSorted}
                                wrapperClasses="table-responsive"
                                {...paginationTableProps}
                                cellEdit={cellEditFactory(cellEditProps)}
                                onTableChange={handleTableChange}
                                noDataIndication={() =>
                                  !props.CigarManager.loadingVitolas ? (
                                    <NoDataIndicator message="No vitolas found matching search query." />
                                  ) : (
                                    <TableLoader />
                                  )
                                }
                                {...toolkitprops.baseProps}
                              />
                              <PaginationTotalStandalone {...paginationProps} />
                              <div
                                style={{
                                  width: 200,
                                  display: 'inline-block',
                                  float: 'right',
                                }}
                              />
                              <div
                                style={{ display: 'flex', paddingBottom: 20 }}
                              >
                                <div style={{ flex: 1 }}>
                                  <StandalonePagination
                                    paginationProps={paginationProps}
                                  />
                                </div>
                                <div>
                                  <SizePerPageDropdownStandalone
                                    {...paginationProps}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </PaginationProvider>
                      )}
                    </div>
                  )}
                </ToolkitProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderAddVitolaModal()}
      {dialogModalComponent()}
      {renderFilterOptions()}
      {toggler && selectedLightboxImageUrl && (
        <Lightbox
          mainSrc={selectedLightboxImageUrl}
          onCloseRequest={() => setToggler(false)}
        />
      )}
      <VitolaMerger
        open={showMergeModal}
        sourceVitola={selectedVitola}
        onClose={() => setShowMergeModal(false)}
        onCancel={() => {
          setSelectedVitola(null);
          setShowMergeModal(false);
        }}
        onMerged={(vitola) => {
          // TODO Update the vitola details in the list?
          handleRowDelete(selectedVitola);
          setSelectedVitola(null);
          setShowMergeModal(false);
        }}
      />
    </div>
  );
};

const mapStateToProps = ({ CigarManager }) => ({ CigarManager });

export default connect(mapStateToProps, {
  getAllVitolas,
})(VitolaTable);
